import JSZip from "jszip";

// Builds an .xlsx (a zip of XML parts) with JSZip, which the app already ships,
// so the translation export needs no extra dependency.

const MISSING_VALUE = "missing_translation";
const SHEET_NAME = "Translations";

// cellXfs indexes in STYLES_XML below. The sheet is protected, so only styles
// with <protection locked="0"/> (MISSING_STYLE, EDITABLE_STYLE) can be edited.
const HEADER_STYLE = 1;
const TEXT_STYLE = 2;
export const MISSING_STYLE = 3;
const EDITABLE_STYLE = 4;

// English is the read-only reference; every other exported language is editable.
const REFERENCE_LANGUAGE = "en";

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FFFFF2CC"/><bgColor indexed="64"/></patternFill></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="2" borderId="0" xfId="0" applyFill="1" applyAlignment="1" applyProtection="1"><alignment vertical="top" wrapText="1"/><protection locked="0"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1" applyProtection="1"><alignment vertical="top" wrapText="1"/><protection locked="0"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${SHEET_NAME}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

const WORKBOOK_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

const escapeXml = value =>
  String(value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// 0 -> A, 25 -> Z, 26 -> AA
const columnName = index => {
  let name = "";
  let n = index;
  while (n >= 0) {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  }
  return name;
};

// Flattens { group: { section: { key: "text" } } } into
// [{ group, section, key, path: [..] }].
const flatten = (node, path = []) =>
  Object.keys(node || {}).flatMap(name => {
    const nextPath = [...path, name];
    const value = node[name];

    if (value !== null && typeof value === "object") {
      return flatten(value, nextPath);
    }
    return [
      {
        group: nextPath[0],
        section: nextPath.slice(1, -1).join("."),
        key: nextPath[nextPath.length - 1],
        path: nextPath,
      },
    ];
  });

const getAtPath = (obj, path) =>
  path.reduce(
    (acc, part) => (acc !== null && acc !== undefined ? acc[part] : undefined),
    obj,
  );

// Languages to export for a translator: English (reference) first, then the
// allowed language, or every language when the translator is allowed "all".
export const resolveExportLanguages = (allowedLanguage, availableLanguages) => {
  const wanted =
    allowedLanguage === "all"
      ? availableLanguages
      : [allowedLanguage].filter(lang => availableLanguages.includes(lang));

  return ["en", ...wanted.filter(lang => lang !== "en")];
};

const buildCell = (rowIndex, colIndex, text, style) =>
  `<c r="${columnName(colIndex)}${rowIndex}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
    text,
  )}</t></is></c>`;

const buildRow = (rowIndex, cells) =>
  `<row r="${rowIndex}">${cells
    .map(({ text, style }, colIndex) =>
      buildCell(rowIndex, colIndex, text, style),
    )
    .join("")}</row>`;

const READ_ONLY_ERROR_TITLE = "Read-only column";
const READ_ONLY_ERROR =
  "Group, Section, Key and EN are read-only. Please edit only the language columns.";

// Sheet protection is ignored by Google Sheets on import, but data validation is
// kept. A "custom" rule that is always false makes Sheets reject typed input in
// the read-only columns (Group, Section, Key and EN).
const buildReadOnlyValidationXml = (rowCount, languages) => {
  const readOnlyColumns = [
    0,
    1,
    2,
    ...languages
      .map((lang, i) => (lang === REFERENCE_LANGUAGE ? i + 3 : -1))
      .filter(i => i >= 0),
  ];
  const sqref = readOnlyColumns
    .map(col => `${columnName(col)}1:${columnName(col)}${rowCount}`)
    .join(" ");

  return `<dataValidations count="1"><dataValidation type="custom" errorStyle="stop" allowBlank="0" showErrorMessage="1" errorTitle="${READ_ONLY_ERROR_TITLE}" error="${escapeXml(
    READ_ONLY_ERROR,
  )}" sqref="${sqref}"><formula1>FALSE</formula1></dataValidation></dataValidations>`;
};

const buildSheetXml = (rows, languages) => {
  const widths = [24, 24, 32, ...languages.map(() => 55)];
  const cols = widths
    .map(
      (width, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`,
    )
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${cols}</cols>
<sheetData>${rows.join("")}</sheetData>
<sheetProtection sheet="1" objects="1" scenarios="1" formatColumns="0" formatRows="0"/>
${buildReadOnlyValidationXml(rows.length, languages)}
</worksheet>`;
};

// translations: { en: {...}, fr: {...} } as stored in localStorage["translation"].
// languages: codes to export as columns, e.g. ["en", "fr"].
// Resolves to the .xlsx file bytes (Uint8Array).
export const buildTranslationsXlsx = async (translations, languages) => {
  const header = [
    { text: "Group", style: HEADER_STYLE },
    { text: "Section", style: HEADER_STYLE },
    { text: "Key", style: HEADER_STYLE },
    ...languages.map(lang => ({
      text: lang.toUpperCase(),
      style: HEADER_STYLE,
    })),
  ];

  const rows = [buildRow(1, header)];

  // English is the source of truth for which keys exist.
  flatten(translations.en).forEach((entry, i) => {
    const cells = [
      { text: entry.group, style: TEXT_STYLE },
      { text: entry.section, style: TEXT_STYLE },
      { text: entry.key, style: TEXT_STYLE },
      ...languages.map(lang => {
        const value = getAtPath(translations[lang], entry.path);
        const text = value === undefined || value === null ? "" : value;

        if (lang === REFERENCE_LANGUAGE) return { text, style: TEXT_STYLE };
        return {
          text,
          style: text === MISSING_VALUE ? MISSING_STYLE : EDITABLE_STYLE,
        };
      }),
    ];

    rows.push(buildRow(i + 2, cells));
  });

  const zip = new JSZip();
  zip.file("[Content_Types].xml", CONTENT_TYPES_XML);
  zip.file("_rels/.rels", ROOT_RELS_XML);
  zip.file("xl/workbook.xml", WORKBOOK_XML);
  zip.file("xl/_rels/workbook.xml.rels", WORKBOOK_RELS_XML);
  zip.file("xl/styles.xml", STYLES_XML);
  zip.file("xl/worksheets/sheet1.xml", buildSheetXml(rows, languages));

  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
};
